import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  CreditCard,
  Crown,
  Users,
  Lightning,
  Check,
  Warning,
  Minus,
  Plus,
  ArrowClockwise,
} from "@phosphor-icons/react";
import { useAuth, API } from "@/contexts/AuthContext";

const EUR = (n) =>
  new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(n);

const dateDE = (iso) => {
  if (!iso) return ",";
  try {
    return new Date(iso).toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

export default function BillingPage() {
  const { user, refresh } = useAuth();
  const [pricing, setPricing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [extraSeatsToAdd, setExtraSeatsToAdd] = useState(1);
  const [notifications, setNotifications] = useState([]);

  const sub = user?.subscription;
  const isOwner = user?.role === "owner";
  const isPro = sub?.tier === "pro";

  useEffect(() => {
    axios.get(`${API}/pricing`).then((r) => setPricing(r.data)).catch(() => {});
    axios.get(`${API}/app/notifications/subscription`).then((r) => setNotifications(r.data?.notifications || [])).catch(() => {});
  }, [user]);

  const daysLeft = useMemo(() => {
    if (!sub?.current_period_end) return null;
    const diff = new Date(sub.current_period_end) - new Date();
    return Math.floor(diff / (1000 * 60 * 60 * 24));
  }, [sub]);

  const startCheckout = async (intent, extra_seats = 0) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/app/stripe/create-checkout-session`, {
        intent,
        extra_seats,
        origin_url: window.location.origin,
      });
      if (data?.url) window.location.href = data.url;
      else if (data?.seats_added) {
        toast.success(`${data.new_extra_seats} zusätzliche Sitze aktiv`);
        await refresh();
        setBusy(false);
      }
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Checkout fehlgeschlagen.");
      setBusy(false);
    }
  };

  const cancelSub = async () => {
    if (!window.confirm("Möchtest du dein Abo wirklich zum Ende der Laufzeit kündigen? Du behältst bis dahin vollen Pro-Zugang.")) return;
    setBusy(true);
    try {
      await axios.post(`${API}/app/stripe/cancel`, {});
      await refresh();
      toast.success("Kündigung vorgemerkt. Dein Abo endet zum Laufzeitende.");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Kündigung fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  const resumeSub = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/app/stripe/resume`, {});
      await refresh();
      toast.success("Willkommen zurück, dein Abo verlängert sich wieder automatisch.");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Reaktivierung fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  const proratedSeatCost = useMemo(() => {
    if (!pricing || !sub?.current_period_end) return null;
    const daysRemain = Math.max(1, daysLeft || 0);
    const perSeatProrated = (pricing.pro.extra_seat_price_year * daysRemain) / 365;
    return extraSeatsToAdd * perSeatProrated;
  }, [pricing, sub, daysLeft, extraSeatsToAdd]);

  return (
    <div className="max-w-4xl space-y-8" data-testid="billing-page">
      <header>
        <h1 className="font-heading text-3xl font-semibold text-forest-900">Abonnement</h1>
        <p className="font-body text-[15px] text-[#4A524C] mt-1">
          Tarif, Sitze und Verlängerungen deines Betriebs.
        </p>
      </header>

      {/* Notifications */}
      {notifications.map((n, i) => (
        <div
          key={i}
          data-testid="billing-notification"
          className={`rounded-2xl border p-4 flex items-start gap-3 ${
            n.severity === "warning"
              ? "border-amber-300 bg-amber-50"
              : "border-forest-200 bg-forest-50"
          }`}
        >
          <Warning size={20} weight="fill" className={n.severity === "warning" ? "text-amber-700" : "text-forest-700"} />
          <div className="flex-1 text-sm">
            <div className="font-heading font-semibold text-forest-900">{n.message}</div>
            <div className="text-[#4A524C] mt-0.5">Fällig: {dateDE(n.renews_on)}</div>
            {isOwner && (
              <button
                onClick={() => startCheckout("renew", 0)}
                disabled={busy}
                className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-forest-700 hover:underline"
              >
                Jetzt verlängern <ArrowClockwise size={14} weight="bold" />
              </button>
            )}
          </div>
        </div>
      ))}

      {/* Current Plan */}
      <section className="bg-white rounded-3xl border border-bone-200 p-6 sm:p-8" data-testid="billing-current">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-[#7A827C]">Aktueller Tarif</div>
            <div className="mt-1 flex items-center gap-3">
              <h2 className="font-heading text-3xl font-semibold text-forest-900">
                {isPro ? "Plantus Pro" : "Plantus Free"}
              </h2>
              {isPro && (
                <span className="inline-flex items-center gap-1 bg-forest-50 text-forest-700 text-xs font-semibold uppercase tracking-[0.14em] px-2.5 py-1 rounded-full">
                  <Crown size={12} weight="fill" /> Pro
                </span>
              )}
            </div>
            <div className="mt-2 text-sm text-[#4A524C]">
              {isPro && sub?.current_period_end
                ? sub?.cancel_at_period_end
                  ? `Endet am ${dateDE(sub.current_period_end)} (Kündigung vorgemerkt).`
                  : `Verlängert sich automatisch am ${dateDE(sub.current_period_end)}${
                      daysLeft != null ? ` (noch ${daysLeft} Tage)` : ""
                    }.`
                : "Du nutzt aktuell den Free-Tarif."}
            </div>
          </div>
          <div className="text-right">
            {isPro && sub?.last_payment_amount_eur != null && (
              <>
                <div className="font-heading text-2xl font-semibold text-forest-900">
                  {EUR(sub.last_payment_amount_eur)}
                </div>
                <div className="text-xs text-[#7A827C]">letzte Zahlung</div>
              </>
            )}
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Stat
            icon={Lightning}
            label="Anwendungen"
            warn={!isPro && (sub?.applications_used ?? 0) >= (sub?.applications_limit ?? 3)}
            value={
              isPro
                ? `${sub?.applications_used ?? 0} · unbegrenzt`
                : `${sub?.applications_used ?? 0} / ${sub?.applications_limit ?? 3}`
            }
          />
          <Stat
            icon={Users}
            label="Mitarbeiter"
            warn={(sub?.seats_used ?? 0) > (sub?.seats_total ?? 3)}
            value={`${sub?.seats_used ?? 0} / ${sub?.seats_total ?? 3}`}
          />
          <Stat icon={CreditCard} label="Status" value={isPro ? (sub?.status || "active") : "free"} />
        </div>

        <div className="mt-7 flex flex-wrap gap-3">
          {!isPro && isOwner && (
            <Link
              to="/preise"
              data-testid="billing-go-pricing"
              className="inline-flex items-center gap-2 bg-forest-600 hover:bg-forest-700 text-white font-medium text-sm px-5 py-3 rounded-full shadow-sm hover:shadow-md transition-all"
            >
              <Crown size={16} weight="fill" /> Auf Pro upgraden
            </Link>
          )}
          {isPro && isOwner && (
            <button
              onClick={() => startCheckout("renew", 0)}
              disabled={busy}
              data-testid="billing-renew"
              className="inline-flex items-center gap-2 bg-forest-600 hover:bg-forest-700 text-white font-medium text-sm px-5 py-3 rounded-full shadow-sm hover:shadow-md"
            >
              <ArrowClockwise size={16} weight="bold" />
              Jetzt um 1 Jahr verlängern{pricing ? ` (${EUR(pricing.pro.price_year + (sub?.extra_seats || 0) * pricing.pro.extra_seat_price_year)})` : ""}
            </button>
          )}
          {!isOwner && (
            <div className="text-sm text-[#7A827C]">Nur der Betriebsinhaber kann das Abo verwalten.</div>
          )}
        </div>
      </section>

      {/* Add seats */}
      {isPro && isOwner && pricing && (
        <section className="bg-white rounded-3xl border border-bone-200 p-6 sm:p-8" data-testid="billing-seats">
          <h3 className="font-heading text-xl font-semibold text-forest-900">Zusätzliche Mitarbeiter hinzubuchen</h3>
          <p className="text-sm text-[#4A524C] mt-1">
            Jeder zusätzliche Sitz kostet {EUR(pricing.pro.extra_seat_price_year)} pro Jahr. Wir rechnen anteilig fuer die verbleibende Laufzeit ab.
          </p>

          <div className="mt-6 rounded-2xl bg-bone/60 border border-bone-200 p-5 flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-[#7A827C]">Sitze hinzubuchen</div>
              <div className="font-heading text-2xl font-semibold text-forest-900">
                +{extraSeatsToAdd} <span className="text-base text-[#7A827C]">/ neu gesamt {(sub?.seats_total || 3) + extraSeatsToAdd}</span>
              </div>
              {proratedSeatCost != null && (
                <div className="text-xs text-[#4A524C] mt-1">
                  Einmalig anteilig fuer {daysLeft} Tage: <strong>{EUR(proratedSeatCost)}</strong>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setExtraSeatsToAdd((d) => Math.max(1, d - 1))}
                disabled={extraSeatsToAdd <= 1}
                data-testid="billing-add-seat-minus"
                className="w-10 h-10 rounded-full bg-white border border-bone-200 hover:bg-bone disabled:opacity-40 flex items-center justify-center"
                aria-label="Weniger"
              >
                <Minus size={16} weight="bold" />
              </button>
              <button
                onClick={() => setExtraSeatsToAdd((d) => d + 1)}
                data-testid="billing-add-seat-plus"
                className="w-10 h-10 rounded-full bg-white border border-bone-200 hover:bg-bone flex items-center justify-center"
                aria-label="Mehr"
              >
                <Plus size={16} weight="bold" />
              </button>
              <button
                onClick={() => startCheckout("add_seats", extraSeatsToAdd)}
                disabled={busy}
                data-testid="billing-add-seat-checkout"
                className="ml-2 inline-flex items-center gap-2 bg-forest-600 hover:bg-forest-700 disabled:bg-forest-600/40 text-white font-medium text-sm px-5 py-2.5 rounded-full"
              >
                <Check size={16} weight="bold" /> Bezahlen
              </button>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

const Stat = ({ icon: Icon, label, value, warn }) => (
  <div className={`rounded-2xl border p-4 ${warn ? "bg-amber-50 border-amber-300" : "bg-bone/60 border-bone-200"}`}>
    <div className={`flex items-center gap-2 text-xs uppercase tracking-[0.18em] ${warn ? "text-amber-800" : "text-[#7A827C]"}`}>
      <Icon size={14} weight="duotone" /> {label}
    </div>
    <div className={`font-heading text-lg font-semibold mt-1 ${warn ? "text-amber-900" : "text-forest-900"}`}>{value}</div>
  </div>
);
